import React from 'react'
import PropTypes from 'prop-types'
import { Box, Tooltip, Typography, useTheme } from '@material-ui/core'
import { FilterChip } from '../styles/StyledComponents'

// The "Tumour RNA" choice of the somatic wizard (roadmap R4.1): the card of the
// first somatic step and the chip of the mode switch. Offered only where the
// analysis start would accept an RNA analysis (`availability` from
// rnaAvailability(GET rna-vocabulary)); elsewhere both are disabled and say why,
// so nobody fills in the whole wizard to be refused at submit.

const availabilityShape = PropTypes.shape({ enabled: PropTypes.bool, reason: PropTypes.string })

export const TumourRnaCard = ({ availability, onSelect }) => {
  const theme = useTheme()
  const enabled = availability?.enabled !== false
  return (
    <Box
      sx={{
        flex: 1,
        p: 2,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: enabled ? theme.palette.background.paper : theme.palette.action.disabledBackground,
        transition: 'all 0.2s ease',
        cursor: enabled ? 'pointer' : 'not-allowed',
        opacity: enabled ? 1 : 0.7,
        '&:hover': enabled
          ? {
              borderColor: theme.palette.primary.main,
              backgroundColor: theme.palette.primary.main + '08',
              transform: 'translateY(-2px)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            }
          : {},
      }}
      data-testid="somatic-type-tumor-rna"
      role="button"
      aria-disabled={!enabled}
      onClick={enabled ? onSelect : undefined}
    >
      <Typography
        variant="subtitle1"
        sx={{ fontWeight: 600, mb: 1, color: enabled ? theme.palette.primary.main : theme.palette.text.secondary }}
      >
        Tumour RNA Analysis
      </Typography>
      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
        Fusions and gene expression from tumour RNA (whole transcriptome), tumour-only.
      </Typography>
      {enabled ? (
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>
          Requires: 1 RNA FASTQ pair (short read)
        </Typography>
      ) : (
        <Typography
          variant="caption"
          data-testid="somatic-type-tumor-rna-unavailable"
          sx={{ color: theme.palette.warning.main, fontWeight: 600 }}
        >
          {availability.reason}
        </Typography>
      )}
    </Box>
  )
}

TumourRnaCard.propTypes = {
  availability: availabilityShape,
  onSelect: PropTypes.func.isRequired,
}

TumourRnaCard.defaultProps = {
  availability: { enabled: true, reason: null },
}

export const TumourRnaChip = ({ availability, active, onSelect }) => {
  const enabled = availability?.enabled !== false
  const chip = (
    <FilterChip
      active={active}
      data-testid="somatic-mode-tumor-rna"
      aria-disabled={!enabled}
      onClick={enabled ? onSelect : undefined}
      sx={enabled ? undefined : { opacity: 0.5, cursor: 'not-allowed', '&:hover': { transform: 'none' } }}
    >
      Tumour RNA
    </FilterChip>
  )
  return enabled ? (
    chip
  ) : (
    <Tooltip title={availability.reason} arrow>
      <span>{chip}</span>
    </Tooltip>
  )
}

TumourRnaChip.propTypes = {
  availability: availabilityShape,
  active: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
}

TumourRnaChip.defaultProps = {
  availability: { enabled: true, reason: null },
  active: false,
}
